import { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { useParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { nanoid } from 'nanoid';
import { formatDistanceToNow } from 'date-fns';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import X from '@components/Icons/X';
import { CommonUserInboxItem } from '@typings/api-models';
import TeamsContext from '@/providers/Teams/TeamsContext';
import useSocketContext from '@/providers/Socket/useSocketContext';
import { isInboxUpdatedMessageData, SocketMessageType } from '@/providers/Socket';
import { logError } from '@/providers/ErrorTracking';
import { HookResponse, PAGE_SIZE } from './common';
import DashboardContext from '../../DashboardContext';
import pickNewSelectedId from './pickNewSelectedId';

const getDetailPagePath: HookResponse['getDetailPagePath'] = ({ id }) => getUrl(IDENTIFIERS.DASHBOARD_INBOX_ITEM, { id });
type Item = HookResponse['items'][number];

const inboxItemToListItem = (
  { item, onDismiss, onRead }: {
    item: CommonUserInboxItem;
    onDismiss: (inboxItemId: string,taskId?:string) => void;
    onRead: (inboxItemId: string) => void;
  },
): Item => {
  let bottomLine;
  let topLine;
  let label;
  let inboxType: 'noodle-broadcast' |'chat' | 'comment' | 'task' | 'notification' | undefined;
  let href;
  switch (item.type) {
  case 'chat':
    inboxType = 'chat';
    topLine = "Private Message";
    label = 'Respond to message';
    bottomLine = `New message from ${item.sender?.name ?? ''}`;
    href = getUrl(IDENTIFIERS.DASHBOARD_INBOX_ITEM, { id: item.id });
    break;
  case 'workflow-comment':
    inboxType = 'comment';
    label = 'Respond to comment';
    bottomLine = `New comment from ${item.sender?.name ?? ''}`;
    topLine = item.task?.userWorkflow?.workflow.name ?? item.meta?.workflowName ?? null;
    href = getUrl(IDENTIFIERS.DASHBOARD_INBOX_ITEM, { commentId: item.task?.referenceId, id: item.id });
    break;
  case 'payment-failed':
    label = 'Payment Failed';
    bottomLine = `Notification from ${item.sender?.name ?? ''}`;
    href = getUrl(IDENTIFIERS.DASHBOARD_INBOX_ITEM, { commentId: item.task?.referenceId, id: item.id });
    break;
  case 'task':
    inboxType = 'task';
    label = item.task?.title ?? 'A new task';
    bottomLine = `For ${item.task?.userWorkflow?.person.name ?? ''}`;
    topLine = item.task?.userWorkflow?.workflow.name ?? null;
    href = getUrl(IDENTIFIERS.DASHBOARD_INBOX_ITEM, { id: item.id });
    break;
  case 'noodle-broadcast':
    inboxType = 'noodle-broadcast';
    label = item.meta?.title ?? null;
    bottomLine = item.meta?.message ?? null;
    topLine = 'Product Announcement';
    href = getUrl(IDENTIFIERS.DASHBOARD_INBOX_ITEM, { id: item.id });
    break;
  case 'notification':
    inboxType = 'notification';
    label = item.meta?.clientName ?? 'New notification';
    bottomLine = `E-Filing ${item.meta?.status}`;
    topLine = 'Notification';
    href = getUrl(IDENTIFIERS.DASHBOARD_INBOX_ITEM, { id: item.id });
    break;
  default:
    label = 'A new item';
    break;
  }

  return {
    avatar: undefined,
    bottomLine,
    href,
    id: item.id,
    inboxType,
    isAi: false,
    isMuted: item.isRead,
    label,
    onClick: () => onRead(item.id),
    secondaryActions: {
      ariaLabel: 'Actions',
      compact: true,
      onSelect: () => onDismiss(item.id, item.task?.id),
      options: [{
        destructive: true,
        icon: X,
        value: 'Dismiss',
      }]},
    time: formatDistanceToNow(new Date(item.createdAt), { addSuffix: true }),
    topLine,
  };
};

const useInboxRequest = ({
  search,
  types,
}: {
  search: string | null;
  types?: string[];
}): HookResponse => {
  const router = useRouter();

  const [isFetching, setIsFetching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const pathParams = useParams<{ id?: string }>();
  const [items, setItems] = useState<HookResponse['items']>([]);
  const mostRecentRequestRef = useRef<string | null>(null);
  const { creatorId } = useContext(TeamsContext);
  const { refetchUnreadInboxItemCount } = useContext(DashboardContext);

  const { getData: getCreatorInboxItemsFn } = useNoodleApi(tsClient.creators.getCreatorInboxItems);
  const { getData: dismissInboxItemFn } = useNoodleApi(tsClient.inbox.dismissInboxItem);
  const { getData: changeReadStatusFn } = useNoodleApi(tsClient.inbox.changeReadStatus);
  const { getData: dismissTask } = useNoodleApi(tsClient.tasks.dismissTask);

  const { addListener, removeListener } = useSocketContext();

  const selectedId = pathParams?.id ?? null;
  const selectedIdRef = useRef<string | null>(selectedId);
  selectedIdRef.current = pathParams?.id ?? null;

  const loadPage = useCallback(async ({
    page, search: newSearch, types: newTypes,
  }: { search:string | null, page: number, types?: string[] }): Promise<void> => {
    if (!creatorId) {
      return;
    }

    setCurrentPage(page);
    const thisRequestId = nanoid();
    mostRecentRequestRef.current = thisRequestId;

    setIsFetching(true);
    setItems([]);
    const response = await getCreatorInboxItemsFn({
      creatorId,
      page,
      perPage: PAGE_SIZE,
      search: newSearch ?? undefined,
      types: newTypes && newTypes.length > 0 ? newTypes.join(',') : undefined,
    });

    if (mostRecentRequestRef.current === thisRequestId) {
      if (response.data) {
        setItems(response.data.items.map((item) => inboxItemToListItem({
          item,
          // eslint-disable-next-line no-use-before-define
          onDismiss: handleDismissItem,
          // eslint-disable-next-line no-use-before-define
          onRead: handleReadItem,
        })));
        setNumPages(response.data.numPages);
      }
      setIsFetching(false);
    }
  }, [
    getCreatorInboxItemsFn,
    dismissInboxItemFn,
    creatorId,
  ]);

  // This is added to the item in loadPage, so the components have an old version of the function.
  // This old version has the old state (so can't use selectedId for example because it's old)
  const handleDismissItem = async (dismissedItemId: string,dismissedTaskId?:string): Promise<void> => {
    const response = await dismissInboxItemFn({ inboxItemId: dismissedItemId });
    if(dismissedTaskId) {
      await dismissTask({taskId:dismissedTaskId});
    }

    if (response.data) {
      let newSelectedId: string | null = null;

      setItems(prevState => {
        newSelectedId = pickNewSelectedId({
          currentSelectedId: selectedIdRef.current,
          dismissedItemId,
          items: prevState,
        });

        return prevState.filter(item => item.id !== dismissedItemId);
      });

      if (newSelectedId !== selectedIdRef.current) {
        let newUrl;
        if (!newSelectedId) {
          newUrl = getUrl(IDENTIFIERS.DASHBOARD_INBOX);
        } else {
          newUrl = getUrl(IDENTIFIERS.DASHBOARD_INBOX_ITEM, { id: newSelectedId });
        }
        router.push(newUrl);
      }
    }
  };

  // This is added to the item in loadPage, so the components have an old version of the function.
  // This old version has the old state (so can't use selectedId for example because it's old)
  const handleReadItem = async (inboxItemId: string): Promise<void> => {
    let shouldUpdate = false;

    setItems((prevState) => prevState.map(item => {
      if (item.id === inboxItemId && !item.isMuted) {
        shouldUpdate = true;
        return {
          ...item,
          isMuted: true,
        };
      }
      return item;
    }));

    if (shouldUpdate) {
      await changeReadStatusFn({ inboxItemId, isRead: true });
      refetchUnreadInboxItemCount();
    }
  };

  useEffect(() => {
    loadPage({ page: 1, search, types });
  }, [loadPage, search, types]);

  const pagination: HookResponse['pagination'] = {
    numPages,
    onChangePage: (newPage) => {
      loadPage({ page: newPage, search, types });
    },
    page: currentPage,
  };

  useEffect(() => {
    const handleInboxStatusChange = async (data: unknown): Promise<void> => {
      if (isInboxUpdatedMessageData(data)) {
        const { inboxItemId } = data;
        let isReloadNeeded = false;
        let pageToLoad = 1;
        // use setItems to get current state without needing it in the dependency list or a ref
        if (data.action === 'dismissed' || data.action === 'removed') {
          // reload if the dismissed/removed item is in the current page.
          setItems(prevState => {
            isReloadNeeded = prevState.some(({ id }) => id === inboxItemId);
            return prevState.filter(({ id }) => id !== inboxItemId);
          });
          setCurrentPage(prevState => {
            pageToLoad = prevState;
            return prevState;
          });
        } else if (data.action === 'added') {
          // reload if on page 1 and there is an item added
          setCurrentPage(prevState => {
            if (prevState === 1) {
              pageToLoad = 1;
              isReloadNeeded = true;
            }
            return prevState;
          });
        }
        if (isReloadNeeded) {
          loadPage({
            page: pageToLoad,
            search,
            types,
          });
        }
      } else {
        const extras = data && typeof data === 'object' ? data : { data };
        logError(new Error('Unsupported UserInboxUpdatedMessageData'), { ...extras });
      }
    };

    const listenerId = addListener({
      fn: handleInboxStatusChange,
      messageType: SocketMessageType.USER_INBOX_UPDATED,
    });

    return () => {
      removeListener(listenerId);
    };
  }, [addListener, removeListener, loadPage, search, types]);

  return useMemo(() => ({
    getDetailPagePath,
    headerActions: [],
    ifNoItemsText: 'No inbox items yet',
    isFetchingList: isFetching,
    items,
    pagination,
    reloadList: () => loadPage({ page: 1, search, types }),
    selectedId,
  }), [
    isFetching,
    items,
    loadPage,
    pagination,
    selectedId,
    search,
    types,
  ]);
};

export default useInboxRequest;
